import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  TextField,
  Button,
  Grid,
  LinearProgress,
  Collapse,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import {
  Lock as LockIcon,
  Save as SaveIcon,
  Power as PowerIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
    height: "100%",
    overflowY: "auto",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  card: {
    background: "white",
    borderRadius: "8px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  lockContainer: {
    textAlign: "center",
    padding: "40px 24px",
  },
  lockIcon: {
    fontSize: "64px",
    color: "#9ca3af",
    marginBottom: "16px",
  },
  passwordInput: {
    marginBottom: "16px",
  },
  statsGrid: {
    marginBottom: "24px",
  },
  statCard: {
    padding: "16px",
    background: "#f9fafb",
    borderRadius: "8px",
    textAlign: "center",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#3b82f6",
    marginBottom: "8px",
  },
  statLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
  },
  progressContainer: {
    marginTop: "16px",
  },
  warningBox: {
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "16px",
  },
  warningText: {
    fontSize: "13px",
    color: "#92400e",
    fontWeight: 600,
  },
  limitReachedBox: {
    background: "#fee2e2",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "16px",
  },
  limitReachedText: {
    fontSize: "14px",
    color: "#991b1b",
    fontWeight: 600,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e8ecf1",
    cursor: "pointer",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },
  sectionIcon: {
    marginRight: "12px",
    color: "#3b82f6",
    fontSize: "24px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1a1a1a",
  },
  expandIcon: {
    transition: "transform 0.3s ease",
  },
  expandIconOpen: {
    transform: "rotate(180deg)",
  },
  formGroup: {
    marginBottom: "16px",
  },
  label: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
    display: "block",
  },
  helpText: {
    fontSize: "12px",
    color: "#999",
    marginTop: "4px",
  },
  infoBox: {
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "16px",
  },
  infoBoxText: {
    fontSize: "12px",
    color: "#0369a1",
    lineHeight: "1.6",
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    background: "#fafbfc",
    borderRadius: "6px",
    border: "1px solid #e8ecf1",
  },
}));

export default function Limits() {
  const classes = useStyles();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [limits, setLimits] = useState({
    companies: {
      maxCompanies: 41,
      currentCompanies: 0,
      remaining: 41,
      limitReached: false,
    },
    whatsappBaileys: {
      maxConnections: 80,
      currentConnections: 0,
      remaining: 80,
      limitReached: false,
    },
    users: {
      maxUsers: 320,
      currentUsers: 0,
      remaining: 320,
      limitReached: false,
    },
  });
  const [newCompanyLimit, setNewCompanyLimit] = useState("");
  const [newBaileysLimit, setNewBaileysLimit] = useState("");
  const [newUsersLimit, setNewUsersLimit] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  // Restart states
  const [expandedRestart, setExpandedRestart] = useState(false);
  const [autoRestartEnabled, setAutoRestartEnabled] = useState(false);
  const [restartIntervalHours, setRestartIntervalHours] = useState(24);
  const [lastRestartAt, setLastRestartAt] = useState(null);
  const [nextRestartAt, setNextRestartAt] = useState(null);
  const [restartLoading, setRestartLoading] = useState(false);

  // Cleanup states
  const [expandedCleanup, setExpandedCleanup] = useState(false);
  const [cleanupEnabled, setCleanupEnabled] = useState(false);
  const [cleanupSchedule, setCleanupSchedule] = useState("0 2 * * *");
  const [cleanupDaysOld, setCleanupDaysOld] = useState(30);
  const [publicDirSize, setPublicDirSize] = useState("0 MB");
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // File size limit states
  const [expandedFileSizeLimit, setExpandedFileSizeLimit] = useState(false);
  const [documentMaxSizeMB, setDocumentMaxSizeMB] = useState(50);
  const [videoMaxSizeMB, setVideoMaxSizeMB] = useState(50);
  const [fileSizeLimitLoading, setFileSizeLimitLoading] = useState(false);

  // Database backup states
  const [expandedDatabaseBackup, setExpandedDatabaseBackup] = useState(false);
  const [backups, setBackups] = useState([]);
  const [databaseBackupLoading, setDatabaseBackupLoading] = useState(false);
  const [backupConfig, setBackupConfig] = useState({
    autoBackupEnabled: true,
    backupIntervalHours: 24,
    lastBackupAt: null,
    nextBackupAt: null,
  });
  const [backupConfigLoading, setBackupConfigLoading] = useState(false);
  const fileInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLimits();
      fetchRestartConfig();
      fetchCleanupConfig();
      fetchFileSizeLimits();
      fetchBackups();
      fetchBackupConfig();
      const interval = setInterval(() => {
        fetchLimits();
        // fetchRestartConfig(); // Removido para evitar sobrescrever configurações
        fetchCleanupConfig();
        fetchFileSizeLimits();
        fetchBackups();
        fetchBackupConfig();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchLimits = async () => {
    try {
      const { data } = await api.get("/company-limits");
      setLimits(data);
    } catch (error) {
      console.error("Erro ao buscar limites:", error);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      toast.error("Digite a senha");
      return;
    }

    setLoading(true);
    try {
      await api.post("/company-limits/verify-password", { password });
      setIsAuthenticated(true);
      setPassword("");
      toast.success("✅ Acesso autorizado!");
    } catch (error) {
      toast.error("❌ Senha incorreta!");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLimit = async (type) => {
    let payload = {};
    
    if (type === "companies") {
      if (!newCompanyLimit || newCompanyLimit < 1) {
        toast.error("Digite um número válido para empresas");
        return;
      }
      payload.maxCompanies = parseInt(newCompanyLimit);
    } else if (type === "baileys") {
      if (!newBaileysLimit || newBaileysLimit < 1) {
        toast.error("Digite um número válido para WhatsApp Baileys");
        return;
      }
      payload.maxApiConnections = parseInt(newBaileysLimit);
    } else if (type === "users") {
      if (!newUsersLimit || newUsersLimit < 1) {
        toast.error("Digite um número válido para usuários");
        return;
      }
      payload.maxUsers = parseInt(newUsersLimit);
    }

    setSavingLimit(true);
    try {
      const { data } = await api.put("/company-limits", payload);
      setLimits(data);
      setNewCompanyLimit("");
      setNewBaileysLimit("");
      setNewUsersLimit("");
      toast.success("✅ Limite atualizado com sucesso!");
    } catch (error) {
      const errorMsg = error?.response?.data?.error || error?.response?.data?.details || error?.message || "Erro ao atualizar limite";
      toast.error(errorMsg);
      console.error("Erro ao atualizar limite:", error?.response?.data || error);
    } finally {
      setSavingLimit(false);
    }
  };

  const fetchRestartConfig = async () => {
    try {
      const { data } = await api.get("/system-restart/config");
      if (data) {
        setAutoRestartEnabled(data.autoRestartEnabled || false);
        setRestartIntervalHours(data.restartIntervalHours || 24);
        setLastRestartAt(data.lastRestartAt);
        setNextRestartAt(data.nextRestartAt);
      }
    } catch (error) {
      console.error("Erro ao buscar configuração de restart:", error);
    }
  };

  const fetchCleanupConfig = async () => {
    try {
      const { data } = await api.get("/file-cleanup/config");
      if (data) {
        setCleanupEnabled(data.isActive || false);
        setCleanupSchedule(data.schedule || "0 2 * * *");
        setCleanupDaysOld(data.daysOld || 30);
        setPublicDirSize(data.dirSize || "0 MB");
      }
    } catch (error) {
      console.error("Erro ao buscar configuração de limpeza:", error);
    }
  };

  const handleSaveRestartConfig = async () => {
    setRestartLoading(true);
    try {
      await api.put("/system-restart/config", {
        autoRestartEnabled,
        restartIntervalHours: parseInt(restartIntervalHours),
      });
      
      toast.success("✅ Configuração salva! Restart automático será implementado em breve.");
    } catch (error) {
      toast.error("❌ Erro ao salvar configuração de restart: " + (error.response?.data?.error || error.message));
      console.error("Erro:", error);
    } finally {
      setRestartLoading(false);
    }
  };

  const handleRestartBackend = async () => {
    const confirmed = window.confirm(
      "⚠️ Tem certeza que deseja reiniciar o backend? O servidor ficará indisponível por alguns segundos."
    );
    if (!confirmed) return;

    setRestartLoading(true);
    try {
      await api.post("/system-restart/restart");
      toast.success("✅ Backend será reiniciado em 3 segundos...");
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (error) {
      toast.info("⏳ Reiniciando servidor... Aguarde alguns segundos.");
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  };

  const handleRestartVPS = async () => {
    const confirmed = window.confirm(
      "⚠️⚠️ ATENÇÃO! Tem certeza que deseja reiniciar a VPS inteira? Todos os serviços ficarão indisponíveis por alguns minutos!"
    );
    if (!confirmed) return;

    setRestartLoading(true);
    try {
      await api.post("/system-restart/vps");
      toast.success("✅ VPS será reiniciada em 5 segundos...");
      setTimeout(() => {
        window.location.reload();
      }, 8000);
    } catch (error) {
      toast.info("⏳ Reiniciando VPS... Aguarde alguns minutos.");
      setTimeout(() => {
        window.location.reload();
      }, 30000);
    }
  };

  const handleToggleCleanup = async (enabled) => {
    setCleanupLoading(true);
    try {
      await api.post("/file-cleanup/toggle", { enabled });
      
      setCleanupEnabled(enabled);
      
      toast.success(enabled ? "✅ Limpeza automática ativada!" : "✅ Limpeza automática desativada!");
    } catch (error) {
      toast.error("Erro ao alternar limpeza: " + (error.response?.data?.error || error.message));
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleSaveCleanupConfig = async () => {
    setCleanupLoading(true);
    try {
      await api.put("/file-cleanup/config", {
        schedule: cleanupSchedule,
        daysOld: parseInt(cleanupDaysOld),
      });
      
      toast.success("✅ Configuração de limpeza salva com sucesso!");
      
      // Atualizar configuração após salvar
      setTimeout(() => {
        fetchCleanupConfig();
      }, 1000);
    } catch (error) {
      toast.error("Erro ao salvar configuração: " + (error.response?.data?.error || error.message));
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleRunCleanupNow = async () => {
    const confirmed = window.confirm(
      `⚠️ Tem certeza que deseja executar a limpeza agora? Serão deletados arquivos com mais de ${cleanupDaysOld} dias.`
    );
    if (!confirmed) return;

    setCleanupLoading(true);
    try {
      await api.post("/file-cleanup/run", { daysOld: parseInt(cleanupDaysOld) });
      toast.success("✅ Limpeza iniciada! Aguarde...");
      
      setTimeout(() => {
        fetchCleanupConfig();
      }, 3000);
    } catch (error) {
      toast.error("Erro ao executar limpeza: " + (error.response?.data?.error || error.message));
    } finally {
      setCleanupLoading(false);
    }
  };

  // Função de análise temporariamente desabilitada
  const handleAnalyzeFiles = async () => {
    toast.info("⚠️ Função de análise temporariamente desabilitada para estabilidade");
  };

  const fetchFileSizeLimits = async () => {
    try {
      const { data } = await api.get("/file-size-limits");
      if (data) {
        setDocumentMaxSizeMB(data.documentMaxSizeMB || 50);
        setVideoMaxSizeMB(data.videoMaxSizeMB || 50);
      }
    } catch (error) {
      console.error("Erro ao buscar limites de tamanho de arquivo:", error);
    }
  };

  const handleSaveFileSizeLimits = async () => {
    if (!documentMaxSizeMB || documentMaxSizeMB < 1) {
      toast.error("Tamanho de documento deve ser maior que 0");
      return;
    }

    if (!videoMaxSizeMB || videoMaxSizeMB < 1) {
      toast.error("Tamanho de vídeo deve ser maior que 0");
      return;
    }

    setFileSizeLimitLoading(true);
    try {
      await api.put("/file-size-limits", {
        documentMaxSizeMB: parseInt(documentMaxSizeMB),
        videoMaxSizeMB: parseInt(videoMaxSizeMB),
      });
      toast.success("✅ Limites de tamanho de arquivo atualizados com sucesso!");
      await fetchFileSizeLimits();
    } catch (error) {
      toast.error("Erro ao atualizar limites: " + (error.response?.data?.error || error.message));
    } finally {
      setFileSizeLimitLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const { data } = await api.get("/database-backup/list");
      setBackups(data.backups || []);
    } catch (error) {
      console.error("Erro ao buscar backups:", error);
    }
  };

  const fetchBackupConfig = async () => {
    try {
      const { data } = await api.get("/database-backup/config");
      setBackupConfig(data);
    } catch (error) {
      console.error("Erro ao buscar configuração de backup:", error);
    }
  };

  const handleCreateBackup = async () => {
    setDatabaseBackupLoading(true);
    try {
      const response = await api.post("/database-backup/create");
      const data = response.data;
      
      if (data.warnings) {
        toast.warning(`⚠️ Backup criado com avisos: ${data.warnings}`);
      } else {
        toast.success("✅ Backup criado com sucesso!");
      }
      
      if (data.tablesIncluded && data.totalTables) {
        toast.info(`📊 Tabelas incluídas: ${data.tablesIncluded}/${data.totalTables}`);
      }
      
      await fetchBackups();
    } catch (error) {
      toast.error("Erro ao criar backup: " + (error.response?.data?.error || error.message));
    } finally {
      setDatabaseBackupLoading(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await api.get(`/database-backup/download/${filename}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("✅ Backup baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar backup: " + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteBackup = async (filename) => {
    const confirmed = window.confirm(`⚠️ Tem certeza que deseja deletar o backup ${filename}?`);
    if (!confirmed) return;

    setDatabaseBackupLoading(true);
    try {
      await api.delete(`/database-backup/${filename}`);
      toast.success("✅ Backup deletado com sucesso!");
      await fetchBackups();
    } catch (error) {
      toast.error("Erro ao deletar backup: " + (error.response?.data?.error || error.message));
    } finally {
      setDatabaseBackupLoading(false);
    }
  };

  const handleRestoreBackup = async (filename) => {
    const confirmed = window.confirm(
      `⚠️⚠️ ATENÇÃO! Tem certeza que deseja restaurar o backup ${filename}?\n\n` +
      `• Todos os dados atuais serão sobrescritos!\n` +
      `• Um backup de segurança será criado automaticamente antes da restauração\n` +
      `• Esta operação não pode ser desfeita!\n\n` +
      `Digite "CONFIRMAR" para prosseguir:`
    );
    
    if (!confirmed) return;

    const confirmText = window.prompt(
      "Para confirmar a restauração, digite exatamente: CONFIRMAR"
    );
    
    if (confirmText !== "CONFIRMAR") {
      toast.error("❌ Restauração cancelada - confirmação incorreta");
      return;
    }

    setDatabaseBackupLoading(true);
    try {
      const response = await api.post("/database-backup/restore", { filename });
      const data = response.data;
      
      toast.success("✅ Banco de dados restaurado com sucesso!");
      
      if (data.preRestoreBackup) {
        toast.info(`💾 Backup de segurança criado: ${data.preRestoreBackup}`);
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      const details = error.response?.data?.details;
      
      toast.error(`❌ Erro ao restaurar backup: ${errorMsg}`);
      if (details) {
        toast.info(`💡 ${details}`);
      }
    } finally {
      setDatabaseBackupLoading(false);
    }
  };

  const handleToggleAutoBackup = async (enabled) => {
    setBackupConfigLoading(true);
    try {
      const { data } = await api.put("/database-backup/config", {
        autoBackupEnabled: enabled,
      });
      setBackupConfig(data);
      toast.success(enabled ? "✅ Backup automático ativado!" : "✅ Backup automático desativado!");
    } catch (error) {
      toast.error("Erro ao alternar backup automático: " + (error.response?.data?.error || error.message));
    } finally {
      setBackupConfigLoading(false);
    }
  };

  const handleUploadBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.name.endsWith('.sql')) {
      toast.error("❌ Por favor, selecione um arquivo .sql válido");
      if (event.target) {
        event.target.value = "";
      }
      return;
    }

    setDatabaseBackupLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/database-backup/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;
      toast.success("✅ " + data.message);

      await fetchBackups();
      if (event.target) {
        event.target.value = "";
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      toast.error("❌ Erro ao enviar backup: " + errorMsg);
      if (event.target) {
        event.target.value = "";
      }
    } finally {
      setDatabaseBackupLoading(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.name.endsWith('.sql')) {
      toast.error("❌ Por favor, selecione um arquivo .sql válido");
      if (event.target) {
        event.target.value = "";
      }
      return;
    }

    setDatabaseBackupLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/database-backup/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;
      toast.success("✅ " + data.message);
      
      if (data.instructions) {
        toast.info("💡 " + data.instructions);
      }

      await fetchBackups();
      if (event.target) {
        event.target.value = "";
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      toast.error("❌ Erro ao importar backup: " + errorMsg);
      if (event.target) {
        event.target.value = "";
      }
    } finally {
      setDatabaseBackupLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <MainContainer>
        <div className={classes.container}>
          <div className={classes.header}>
            <h1 className={classes.title}>🔒 Limites do Sistema</h1>
          </div>

          <div className={classes.card}>
            <div className={classes.lockContainer}>
              <LockIcon className={classes.lockIcon} />
              <h2 style={{ marginBottom: "24px", color: "#1a1a1a" }}>
                Acesso Restrito
              </h2>
              <p style={{ color: "#666", marginBottom: "24px" }}>
                Digite a senha para acessar as configurações de limites
              </p>

              <TextField
                fullWidth
                type="password"
                label="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handlePasswordSubmit()}
                variant="outlined"
                className={classes.passwordInput}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handlePasswordSubmit}
                disabled={loading}
              >
                {loading ? "Verificando..." : "Acessar"}
              </Button>
            </div>
          </div>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className={classes.container}>
        <div className={classes.header}>
          <h1 className={classes.title}>🔒 Limites do Sistema</h1>
        </div>

        {/* LIMITE DE EMPRESAS */}
        <div className={classes.card}>
          <h3 style={{ marginBottom: "16px", color: "#1a1a1a", fontWeight: 600 }}>
            🏢 Limite de Empresas
          </h3>
          
          <Grid container spacing={2} className={classes.statsGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>{limits.companies?.currentCompanies || 0}</div>
                <div className={classes.statLabel}>Empresas Ativas</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>{limits.companies?.maxCompanies || 41}</div>
                <div className={classes.statLabel}>Limite Máximo</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div
                  className={classes.statValue}
                  style={{
                    color: (limits.companies?.remaining || 0) > 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  {limits.companies?.remaining || 0}
                </div>
                <div className={classes.statLabel}>Restante</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>
                  {Math.round(((limits.companies?.currentCompanies || 0) / (limits.companies?.maxCompanies || 41)) * 100)}%
                </div>
                <div className={classes.statLabel}>Utilização</div>
              </div>
            </Grid>
          </Grid>

          <div className={classes.progressContainer}>
            <LinearProgress
              variant="determinate"
              value={((limits.companies?.currentCompanies || 0) / (limits.companies?.maxCompanies || 41)) * 100}
              style={{ height: "12px", borderRadius: "6px" }}
            />
          </div>

          {limits.companies?.remaining <= 2 && !limits.companies?.limitReached && (
            <div className={classes.warningBox}>
              <div className={classes.warningText}>
                ⚠️ Você está próximo do limite de empresas!
              </div>
            </div>
          )}

          {limits.companies?.limitReached && (
            <div className={classes.limitReachedBox}>
              <div className={classes.limitReachedText}>
                🚫 LIMITE DE EMPRESAS ATINGIDO
              </div>
              <div style={{ fontSize: "12px", color: "#7f1d1d", marginTop: "8px" }}>
                Você atingiu o limite máximo de {limits.companies?.maxCompanies} empresas.
              </div>
            </div>
          )}

          <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #e8ecf1" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Novo Limite de Empresas"
                  value={newCompanyLimit}
                  onChange={(e) => setNewCompanyLimit(e.target.value)}
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveLimit("companies")}
                  disabled={savingLimit}
                  style={{ height: "56px" }}
                >
                  {savingLimit ? "Salvando..." : "Salvar"}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>

        {/* LIMITE DE WHATSAPP BAILEYS */}
        <div className={classes.card}>
          <h3 style={{ marginBottom: "16px", color: "#1a1a1a", fontWeight: 600 }}>
            📱 Limite de WhatsApp Baileys (Não Oficial)
          </h3>
          
          <Grid container spacing={2} className={classes.statsGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>{limits.whatsappBaileys?.currentConnections || 0}</div>
                <div className={classes.statLabel}>Conexões Ativas</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>{limits.whatsappBaileys?.maxConnections || 80}</div>
                <div className={classes.statLabel}>Limite Máximo</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div
                  className={classes.statValue}
                  style={{
                    color: (limits.whatsappBaileys?.remaining || 0) > 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  {limits.whatsappBaileys?.remaining || 0}
                </div>
                <div className={classes.statLabel}>Restante</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>
                  {Math.round(((limits.whatsappBaileys?.currentConnections || 0) / (limits.whatsappBaileys?.maxConnections || 80)) * 100)}%
                </div>
                <div className={classes.statLabel}>Utilização</div>
              </div>
            </Grid>
          </Grid>

          <div className={classes.progressContainer}>
            <LinearProgress
              variant="determinate"
              value={((limits.whatsappBaileys?.currentConnections || 0) / (limits.whatsappBaileys?.maxConnections || 80)) * 100}
              style={{ height: "12px", borderRadius: "6px", backgroundColor: "#e0e7ff" }}
              classes={{ bar: { backgroundColor: "#6366f1" } }}
            />
          </div>

          {limits.whatsappBaileys?.remaining <= 5 && !limits.whatsappBaileys?.limitReached && (
            <div className={classes.warningBox}>
              <div className={classes.warningText}>
                ⚠️ Você está próximo do limite de conexões WhatsApp Baileys!
              </div>
            </div>
          )}

          {limits.whatsappBaileys?.limitReached && (
            <div className={classes.limitReachedBox}>
              <div className={classes.limitReachedText}>
                🚫 SERVIDOR INTERNO NÃO COMPORTA MAIS CONEXÃO NÃO OFICIAL
              </div>
              <div style={{ fontSize: "12px", color: "#7f1d1d", marginTop: "8px" }}>
                Você atingiu o limite máximo de {limits.whatsappBaileys?.maxConnections} conexões WhatsApp Baileys.
              </div>
            </div>
          )}

          <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #e8ecf1" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Novo Limite de WhatsApp Baileys"
                  value={newBaileysLimit}
                  onChange={(e) => setNewBaileysLimit(e.target.value)}
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  style={{ height: "56px", backgroundColor: "#6366f1" }}
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveLimit("baileys")}
                  disabled={savingLimit}
                >
                  {savingLimit ? "Salvando..." : "Salvar"}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>

        {/* LIMITE DE USUÁRIOS */}
        <div className={classes.card}>
          <h3 style={{ marginBottom: "16px", color: "#1a1a1a", fontWeight: 600 }}>
            👥 Limite de Usuários
          </h3>
          
          <Grid container spacing={2} className={classes.statsGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>{limits.users?.currentUsers || 0}</div>
                <div className={classes.statLabel}>Usuários Ativos</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>{limits.users?.maxUsers || 320}</div>
                <div className={classes.statLabel}>Limite Máximo</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div
                  className={classes.statValue}
                  style={{
                    color: (limits.users?.remaining || 0) > 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  {limits.users?.remaining || 0}
                </div>
                <div className={classes.statLabel}>Restante</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={classes.statCard}>
                <div className={classes.statValue}>
                  {Math.round(((limits.users?.currentUsers || 0) / (limits.users?.maxUsers || 320)) * 100)}%
                </div>
                <div className={classes.statLabel}>Utilização</div>
              </div>
            </Grid>
          </Grid>

          <div className={classes.progressContainer}>
            <LinearProgress
              variant="determinate"
              value={((limits.users?.currentUsers || 0) / (limits.users?.maxUsers || 320)) * 100}
              style={{ height: "12px", borderRadius: "6px", backgroundColor: "#fce7f3" }}
              classes={{ bar: { backgroundColor: "#ec4899" } }}
            />
          </div>

          {limits.users?.remaining <= 10 && !limits.users?.limitReached && (
            <div className={classes.warningBox}>
              <div className={classes.warningText}>
                ⚠️ Você está próximo do limite de usuários!
              </div>
            </div>
          )}

          {limits.users?.limitReached && (
            <div className={classes.limitReachedBox}>
              <div className={classes.limitReachedText}>
                🚫 LIMITE GLOBAL DE USUÁRIOS ATINGIDO
              </div>
              <div style={{ fontSize: "12px", color: "#7f1d1d", marginTop: "8px" }}>
                Você atingiu o limite máximo de {limits.users?.maxUsers} usuários.
              </div>
            </div>
          )}

          <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #e8ecf1" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Novo Limite de Usuários"
                  value={newUsersLimit}
                  onChange={(e) => setNewUsersLimit(e.target.value)}
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  style={{ height: "56px", backgroundColor: "#ec4899" }}
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveLimit("users")}
                  disabled={savingLimit}
                >
                  {savingLimit ? "Salvando..." : "Salvar"}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Gerenciamento do Sistema */}
        <div className={classes.card}>
          <div 
            className={classes.sectionHeader}
            onClick={() => setExpandedRestart(!expandedRestart)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <PowerIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Gerenciamento do Sistema</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedRestart ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedRestart}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Controle o restart automático e manual do servidor para otimizar performance
            </p>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className={classes.switchContainer}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoRestartEnabled}
                        onChange={(e) => setAutoRestartEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Ativar Restart Automático"
                  />
                </div>
                <div className={classes.helpText}>
                  O servidor será reiniciado automaticamente no intervalo configurado
                </div>
              </Grid>

              {autoRestartEnabled && (
                <Grid item xs={12} md={6}>
                  <div className={classes.formGroup}>
                    <label className={classes.label}>Intervalo de Restart (horas)</label>
                    <TextField
                      fullWidth
                      type="number"
                      value={restartIntervalHours}
                      onChange={(e) => setRestartIntervalHours(e.target.value)}
                      placeholder="24"
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1, max: 168 }}
                    />
                    <div className={classes.helpText}>
                      Intervalo em horas (1-168). Padrão: 24 horas
                    </div>
                  </div>
                </Grid>
              )}

              {lastRestartAt && (
                <Grid item xs={12} md={6}>
                  <div className={classes.formGroup}>
                    <label className={classes.label}>Último Restart</label>
                    <TextField
                      fullWidth
                      type="text"
                      value={new Date(lastRestartAt).toLocaleString("pt-BR")}
                      variant="outlined"
                      size="small"
                      disabled
                      style={{ background: "#f9fafb" }}
                    />
                  </div>
                </Grid>
              )}

              {nextRestartAt && autoRestartEnabled && (
                <Grid item xs={12} md={6}>
                  <div className={classes.formGroup}>
                    <label className={classes.label}>Próximo Restart Agendado</label>
                    <TextField
                      fullWidth
                      type="text"
                      value={new Date(nextRestartAt).toLocaleString("pt-BR")}
                      variant="outlined"
                      size="small"
                      disabled
                      style={{ background: "#f9fafb" }}
                    />
                  </div>
                </Grid>
              )}
            </Grid>

            {autoRestartEnabled && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveRestartConfig}
                disabled={restartLoading}
                style={{ marginTop: "16px", textTransform: "none", fontWeight: 600 }}
              >
                {restartLoading ? "Salvando..." : "Salvar Configuração"}
              </Button>
            )}

            <div className={classes.infoBox} style={{ marginTop: "20px", background: "#fef3c7", borderColor: "#fcd34d" }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#92400e", marginBottom: "8px" }}>
                ⚠️ Restart Manual
              </div>
              <div className={classes.infoBoxText} style={{ color: "#78350f" }}>
                Use os botões abaixo para reiniciar o servidor manualmente. O servidor ficará indisponível por alguns segundos.
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "16px", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={handleRestartBackend}
                disabled={restartLoading}
                style={{ background: "#ef4444", textTransform: "none", fontWeight: 600 }}
              >
                {restartLoading ? "Reiniciando..." : "Reiniciar Serviço de API"}
              </Button>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<PowerIcon />}
                onClick={handleRestartVPS}
                disabled={restartLoading}
                style={{ background: "#7c3aed", textTransform: "none", fontWeight: 600 }}
              >
                {restartLoading ? "Reiniciando..." : "Reiniciar Sistema Operacional"}
              </Button>
            </div>
          </Collapse>
        </div>

        {/* Limpeza de Arquivos */}
        <div className={classes.card}>
          <div 
            className={classes.sectionHeader}
            onClick={() => setExpandedCleanup(!expandedCleanup)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <DeleteIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Limpeza de Arquivos</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedCleanup ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedCleanup}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure a limpeza automática de arquivos antigos no servidor para liberar espaço em disco
            </p>

            <div className={classes.infoBox} style={{ background: "#dbeafe", borderColor: "#93c5fd", marginBottom: "16px" }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e40af", marginBottom: "4px" }}>
                💾 Espaço em Disco
              </div>
              <div style={{ fontSize: "14px", color: "#1e3a8a", fontWeight: 600 }}>
                Diretório public: {publicDirSize}
              </div>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className={classes.switchContainer}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={cleanupEnabled}
                        onChange={(e) => handleToggleCleanup(e.target.checked)}
                        disabled={cleanupLoading}
                        color="primary"
                      />
                    }
                    label="Ativar Limpeza Automática"
                  />
                </div>
                <div className={classes.helpText}>
                  Arquivos antigos serão deletados automaticamente conforme o agendamento
                </div>
              </Grid>

              {cleanupEnabled && (
                <>
                  <Grid item xs={12} md={6}>
                    <div className={classes.formGroup}>
                      <label className={classes.label}>Agendamento (Cron)</label>
                      <TextField
                        fullWidth
                        type="text"
                        value={cleanupSchedule}
                        onChange={(e) => setCleanupSchedule(e.target.value)}
                        placeholder="0 2 * * *"
                        variant="outlined"
                        size="small"
                      />
                      <div className={classes.helpText}>
                        Formato cron (ex: 0 2 * * * = 2:00 AM todos os dias)
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <div className={classes.formGroup}>
                      <label className={classes.label}>Deletar arquivos com mais de (dias)</label>
                      <TextField
                        fullWidth
                        type="number"
                        value={cleanupDaysOld}
                        onChange={(e) => setCleanupDaysOld(e.target.value)}
                        placeholder="30"
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 1, max: 365 }}
                      />
                      <div className={classes.helpText}>
                        Arquivos mais antigos que este período serão deletados
                      </div>
                    </div>
                  </Grid>
                </>
              )}
            </Grid>

            <div style={{ display: "flex", gap: "12px", marginTop: "16px", flexWrap: "wrap" }}>
              {cleanupEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveCleanupConfig}
                  disabled={cleanupLoading}
                  style={{ textTransform: "none", fontWeight: 600 }}
                >
                  {cleanupLoading ? "Salvando..." : "Salvar Configuração"}
                </Button>
              )}

              <Button
                variant="contained"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleRunCleanupNow}
                disabled={cleanupLoading}
                style={{ background: "#f59e0b", textTransform: "none", fontWeight: 600 }}
              >
                {cleanupLoading ? "Limpando..." : "Executar Limpeza Agora"}
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleAnalyzeFiles}
                disabled={cleanupLoading}
                style={{ textTransform: "none", fontWeight: 600 }}
              >
                {cleanupLoading ? "Analisando..." : "Analisar Arquivos Antigos"}
              </Button>
            </div>
          </Collapse>
        </div>

        {/* Limites de Tamanho de Arquivo */}
        <div className={classes.card}>
          <div 
            className={classes.sectionHeader}
            onClick={() => setExpandedFileSizeLimit(!expandedFileSizeLimit)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <DeleteIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Limites de Tamanho de Arquivo</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedFileSizeLimit ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedFileSizeLimit}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure o tamanho máximo permitido para upload de documentos e vídeos
            </p>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Tamanho Máximo de Documentos (MB)</label>
                  <TextField
                    fullWidth
                    type="number"
                    value={documentMaxSizeMB}
                    onChange={(e) => setDocumentMaxSizeMB(e.target.value)}
                    placeholder="50"
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 1, max: 1000 }}
                  />
                  <div className={classes.helpText}>
                    Tamanho máximo em MB para upload de documentos (PDF, DOC, etc)
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Tamanho Máximo de Vídeos (MB)</label>
                  <TextField
                    fullWidth
                    type="number"
                    value={videoMaxSizeMB}
                    onChange={(e) => setVideoMaxSizeMB(e.target.value)}
                    placeholder="50"
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 1, max: 1000 }}
                  />
                  <div className={classes.helpText}>
                    Tamanho máximo em MB para upload de vídeos (MP4, AVI, etc)
                  </div>
                </div>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveFileSizeLimits}
              disabled={fileSizeLimitLoading}
              style={{ marginTop: "16px", textTransform: "none", fontWeight: 600 }}
            >
              {fileSizeLimitLoading ? "Salvando..." : "Salvar Limites"}
            </Button>
          </Collapse>
        </div>

        {/* Backup e Restore do Banco de Dados */}
        <div className={classes.card}>
          <div 
            className={classes.sectionHeader}
            onClick={() => setExpandedDatabaseBackup(!expandedDatabaseBackup)}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <SaveIcon className={classes.sectionIcon} />
              <span className={classes.sectionTitle}>Backup e Restore do Banco de Dados</span>
            </div>
            <ExpandMoreIcon 
              className={`${classes.expandIcon} ${expandedDatabaseBackup ? classes.expandIconOpen : ""}`}
            />
          </div>

          <Collapse in={expandedDatabaseBackup}>
            <p className={classes.helpText} style={{ marginBottom: "16px" }}>
              Configure backup automático e faça backup/restauração manual do banco de dados
            </p>

            {/* Configuração de Backup Automático */}
            <div style={{ marginBottom: "24px", paddingBottom: "24px", borderBottom: "1px solid #e8ecf1" }}>
              <h3 style={{ marginBottom: "16px", color: "#1a1a1a", fontWeight: 600 }}>
                ⏰ Backup Automático
              </h3>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div className={classes.switchContainer}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={backupConfig.autoBackupEnabled}
                          onChange={(e) => {
                            setBackupConfig({ ...backupConfig, autoBackupEnabled: e.target.checked });
                            handleToggleAutoBackup(e.target.checked);
                          }}
                          disabled={backupConfigLoading}
                          color="primary"
                        />
                      }
                      label="Ativar Backup Automático (24 horas)"
                    />
                  </div>
                  <div className={classes.helpText}>
                    O banco de dados será feito backup automaticamente a cada 24 horas às 00:00
                  </div>
                </Grid>

                {backupConfig.lastBackupAt && (
                  <Grid item xs={12} md={6}>
                    <div className={classes.formGroup}>
                      <label className={classes.label}>Último Backup</label>
                      <TextField
                        fullWidth
                        type="text"
                        value={new Date(backupConfig.lastBackupAt).toLocaleString("pt-BR")}
                        variant="outlined"
                        size="small"
                        disabled
                        style={{ background: "#f9fafb" }}
                      />
                    </div>
                  </Grid>
                )}

                {backupConfig.nextBackupAt && backupConfig.autoBackupEnabled && (
                  <Grid item xs={12} md={6}>
                    <div className={classes.formGroup}>
                      <label className={classes.label}>Próximo Backup Agendado</label>
                      <TextField
                        fullWidth
                        type="text"
                        value={new Date(backupConfig.nextBackupAt).toLocaleString("pt-BR")}
                        variant="outlined"
                        size="small"
                        disabled
                        style={{ background: "#f9fafb" }}
                      />
                    </div>
                  </Grid>
                )}
              </Grid>
            </div>

            {/* Backup Manual */}
            <div style={{ marginBottom: "24px", paddingBottom: "24px", borderBottom: "1px solid #e8ecf1" }}>
              <h3 style={{ marginBottom: "16px", color: "#1a1a1a", fontWeight: 600 }}>
                💾 Backup Manual
              </h3>

              <div className={classes.infoBox} style={{ background: "#fef3c7", borderColor: "#fcd34d", marginBottom: "16px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#92400e", marginBottom: "8px" }}>
                  ⚠️ Importante - Como usar o Backup Manual
                </div>
                <div className={classes.infoBoxText} style={{ color: "#78350f" }}>
                  <strong>1. Criar Backup:</strong> Clique em "Criar Backup Agora" para gerar um backup completo<br />
                  <strong>2. Enviar Backup:</strong> Use "Enviar Backup" para fazer upload de um arquivo .sql<br />
                  <strong>3. Restaurar:</strong> Após enviar, clique em "Restaurar" na lista para aplicar o backup<br />
                  <br />
                  <strong>⚠️ ATENÇÃO:</strong> A restauração sobrescreverá todos os dados atuais!<br />
                  Um backup de segurança será criado automaticamente antes da restauração.
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleCreateBackup}
                  disabled={databaseBackupLoading}
                  style={{ textTransform: "none", fontWeight: 600 }}
                >
                  {databaseBackupLoading ? "Criando..." : "Criar Backup Agora"}
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={databaseBackupLoading}
                  style={{ textTransform: "none", fontWeight: 600 }}
                >
                  Enviar Backup
                </Button>

                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".sql"
                  onChange={handleUploadBackup}
                  style={{ display: "none" }}
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".sql"
                  onChange={handleImportBackup}
                  style={{ display: "none" }}
                />
              </div>
            </div>

            {/* Lista de Backups */}
            {backups.length > 0 && (
              <div style={{ marginTop: "24px" }}>
                <h3 style={{ marginBottom: "16px", color: "#1a1a1a", fontWeight: 600 }}>
                  Backups Disponíveis ({backups.length})
                </h3>

                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {backups.map((backup, index) => (
                    <div
                      key={index}
                      style={{
                        background: "#f9fafb",
                        border: "1px solid #e8ecf1",
                        borderRadius: "8px",
                        padding: "12px",
                        marginBottom: "12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a" }}>
                          {backup.filename}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                          {backup.size} • {new Date(backup.createdAt).toLocaleString("pt-BR")}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleDownloadBackup(backup.filename)}
                          disabled={databaseBackupLoading}
                          style={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Baixar
                        </Button>

                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleRestoreBackup(backup.filename)}
                          disabled={databaseBackupLoading}
                          style={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Restaurar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleDeleteBackup(backup.filename)}
                          disabled={databaseBackupLoading}
                          style={{ textTransform: "none", fontWeight: 600, color: "#ef4444", borderColor: "#ef4444" }}
                        >
                          Deletar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {backups.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px", color: "#999" }}>
                <p>Nenhum backup disponível. Crie um novo backup para começar.</p>
              </div>
            )}
          </Collapse>
        </div>
      </div>
    </MainContainer>
  );
}
